import { attach, createApi, guard } from 'effector';
import { useStore } from 'effector-react';
import { captureErrorFx } from 'model/helpers/analytics.model';
import en from '../../assets/i18n/en.json';

import { version } from '../../package.json';
import { RootDomain } from 'model/root.model';
import { LocalizationOptions, SupportLanguage } from 'model/domain';

const localizationOptionsDefault = {
	languageSelector: true,
	language: SupportLanguage.en,
	languageBaseUrl: 'https://cdn.hive.id/verify-js',
};

export function useLocalization() {
	const localization = useStore(localization$);

	return (key: string, object: { [key: string]: string } = {}) => {
		const value = localization[key];
		if (!value) {
			return key;
		}

		return Object.keys(object).reduce((acc, key) => {
			return acc.replace(`{${key}}`, object[key]);
		}, value);
	};
}

export const isLocalizationPopupOpen$ = RootDomain.store(false);
export const { openLocalizationPopup, closeLocalizationPopup } = createApi(isLocalizationPopupOpen$, {
	openLocalizationPopup: () => true,
	closeLocalizationPopup: () => false,
});

export const updateLocalization = RootDomain.event<Partial<LocalizationOptions>>();
export const localizationOptions$ = RootDomain.store<LocalizationOptions>({
	language: null,
	languageBaseUrl: null,
	languageSelector: null,
	languagesWhitelist: null,
	supportedLanguages: null,
});
export const defaultLocalizationOptions$ = RootDomain.store<LocalizationOptions>({
	languageSelector: true,
	language: SupportLanguage.en,
	languageBaseUrl: 'https://cdn.hive.id/verify-js',
	languagesWhitelist: null,
	supportedLanguages: null,
});
export const localization$ = RootDomain.store<{ [key: string]: string }>(null);

export const fetchLocalization = RootDomain.effect<LocalizationOptions, any>();
export const fetchDefaultLocalization = attach({
	source: { options: defaultLocalizationOptions$ },
	mapParams: (_: void, source) => source.options,
	effect: fetchLocalization,
});

if (process.env.NODE_ENV === 'development') {
	fetchLocalization.use(async () => {
		return en;
	});
} else {
	fetchLocalization.use(async (options) => {
		const fallbackUrl = `https://cdn.hive.id/verify-js/${version}/i18n/${options.language}.json`;
		const localizationUrl = options.languageBaseUrl ?
			`${options.languageBaseUrl}/${version}/i18n/${options.language}.json` :
			fallbackUrl;

		try {
			const res = await fetch(localizationUrl, {
				method: 'get',
			});
			return await res.json();
		} catch (e) {
			captureErrorFx(e);
			const res = await fetch(fallbackUrl, {
				method: 'get',
			});
			return await res.json();
		}
	});
}

localizationOptions$
	.on(updateLocalization, (store, options) => {
		const payload = Object
			.entries(options)
			.reduce((acc, [key, value]) => {
				return {
					...acc,
					[key]: value === undefined ? localizationOptionsDefault[key] : value,
				};
			}, {});

		return {
			...store,
			...payload,
		};
	});
localization$
	.on(fetchLocalization.doneData, (_, result) => result)
	.on(fetchDefaultLocalization.doneData, (_, result) => result);

guard({
	source: localizationOptions$,
	filter: options => {
		return Object
			.entries(options)
			.every(([, value]) => value !== null);
	},
	target: fetchLocalization,
});
